import { takeLatest, call, put, select } from "redux-saga/effects";
import { Products } from "@grandvisionhq/api-client";
import { statusSuccess, ApiConfigSelectors, assertApiConfig } from "@grandvisionhq/state";
import { startPrescriptionGlassesConfiguration } from "../../../state/actions.js";
import { getBundleFrame, getBundleLensTypes, getBundleLensType, getBundlePackages, getBundlePackage, getBundleAddOns } from "./selectors.internal.js";
import { selectLensTypeSuccess, selectPackageSuccess } from "../../../configurations/state/configurations/actions.js";
import { fetchLensTypes, fetchPackages, fetchAddOns, fetchLensTypesSuccess, fetchLensTypesFailed, fetchPackagesSuccess, fetchPackagesFailed, fetchAddOnsSuccess, fetchAddOnsFailed } from "./actions.js";
const defined = (v) => typeof v !== "undefined";
function* fetchBundleProducts(productType, skus) {
  if (!skus.size) {
    return {
      products: [],
      tried: skus,
      missing: /* @__PURE__ */ new Set()
    };
  }
  const config = yield select(ApiConfigSelectors.getApiConfig);
  assertApiConfig(config);
  const { results } = yield call(
    Products.query,
    config.productsUrl,
    {
      expand: [
        "masterVariant.prices[*].discounted.discount",
        "variants[*].prices[*].discounted.discount"
      ],
      filters: [`variants.sku:${encodeURIComponent([...skus].map((id) => `"${id}"`).join(","))}`]
    }
  );
  const fetchedSkus = new Set(
    results.reduce((skus2, p) => [...skus2, p.masterVariant.sku, ...(p.variants || []).map((v) => v.sku)], []).filter(defined)
  );
  const missing = new Set([...skus].filter((s) => !fetchedSkus.has(s)));
  return {
    products: results,
    tried: skus,
    missing
  };
}
function* ensureLensTypesAreLoaded({
  payload: { frameSku }
}) {
  yield call(loadMissingLensTypes, frameSku);
}
function* loadMissingLensTypes(frameSku) {
  const frame = yield select(getBundleFrame, {
    frameSku
  });
  if (!frame) {
    return;
  }
  const lensTypes = yield select(
    getBundleLensTypes
  );
  const lensTypeSkusToFetch = [...frame.lensTypeSkus].filter((sku) => !(sku in lensTypes));
  if (lensTypeSkusToFetch.length === 0) return;
  yield put(
    fetchLensTypes({
      frameSku,
      skus: new Set(lensTypeSkusToFetch)
    })
  );
}
function* onFetchLensTypes({
  payload: { skus, frameSku }
}) {
  try {
    const result = yield call(fetchBundleProducts, "lens-type", skus);
    yield put(fetchLensTypesSuccess({ frameSku, ...result }));
  } catch (error) {
    yield put(fetchLensTypesFailed({ frameSku, skus, error }));
  }
}
function* loadMissingPackages(lensTypeSku) {
  const bundleLensType = yield select(
    getBundleLensType,
    { lensTypeSku }
  );
  if (!bundleLensType || !statusSuccess(bundleLensType)) {
    return;
  }
  const packages = yield select(
    getBundlePackages
  );
  const packageSkusToFetch = [...bundleLensType.result.packageSkus].filter(
    (packageSku) => !(packageSku in packages)
  );
  if (packageSkusToFetch.length === 0) return;
  yield put(
    fetchPackages({
      lensTypeSku,
      skus: new Set(packageSkusToFetch)
    })
  );
}
function* ensurePackagesAreLoaded({
  payload: { lensTypeSku }
}) {
  yield call(loadMissingPackages, lensTypeSku);
}
function* onFetchPackages({
  payload: { skus, lensTypeSku }
}) {
  try {
    const result = yield call(fetchBundleProducts, "package", skus);
    yield put(fetchPackagesSuccess({ lensTypeSku, ...result }));
  } catch (error) {
    yield put(fetchPackagesFailed({ lensTypeSku, skus, error }));
  }
}
function* loadMissingAddOns(packageSku) {
  const bundlePackage = yield select(
    getBundlePackage,
    { packageSku }
  );
  if (!bundlePackage || !statusSuccess(bundlePackage)) {
    return;
  }
  const addons = yield select(getBundleAddOns);
  const addOnSkusToFetch = [...bundlePackage.result.addOnSkus].filter(
    (addOnSku) => !(addOnSku in addons)
  );
  if (addOnSkusToFetch.length === 0) return;
  yield put(
    fetchAddOns({
      packageSku,
      skus: new Set(addOnSkusToFetch)
    })
  );
}
function* ensureAddOnsAreLoaded({
  payload: { packageSku }
}) {
  yield call(loadMissingAddOns, packageSku);
}
function* onFetchAddOns({
  payload: { skus, packageSku }
}) {
  try {
    const result = yield call(fetchBundleProducts, "addon", skus);
    yield put(fetchAddOnsSuccess({ packageSku, ...result }));
  } catch (error) {
    yield put(fetchAddOnsFailed({ packageSku, skus, error }));
  }
}
const listenForSelectionChanges = [
  takeLatest(startPrescriptionGlassesConfiguration.type, ensureLensTypesAreLoaded),
  takeLatest(selectLensTypeSuccess.type, ensurePackagesAreLoaded),
  takeLatest(selectPackageSuccess.type, ensureAddOnsAreLoaded)
];
const listenForFetchEvents = [
  takeLatest(fetchLensTypes.type, onFetchLensTypes),
  takeLatest(fetchPackages.type, onFetchPackages),
  takeLatest(fetchAddOns.type, onFetchAddOns)
];
const bundleSagas = [...listenForFetchEvents, ...listenForSelectionChanges];
export {
  bundleSagas as default,
  fetchBundleProducts,
  loadMissingAddOns,
  loadMissingLensTypes,
  loadMissingPackages,
  onFetchAddOns,
  onFetchLensTypes,
  onFetchPackages
};
//# sourceMappingURL=sagas.js.map
